import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { ChevronDown, MapPin, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAnchoredPosition } from '@/hooks/useAnchoredPosition'
import { useCities } from '@/hooks/api/useCities'
import { useCityStore } from '@/store/cityStore'
import { buildPropertyHref } from '@/lib/propertyNavLinks'
import { isSeoLandingPathname } from '@/lib/seoLandingPath'
import { cn } from '@/lib/utils'
import { containsTarget } from '@/lib/dom'
import { PATHS } from '@/routes/paths'

interface CitySelectorProps {
  /** @deprecated use variant="select" */
  compact?: boolean
  /** @deprecated use variant="header" */
  header?: boolean
  variant?: 'select' | 'header' | 'hero'
  className?: string
}

export function CitySelector({ compact, header, variant, className }: CitySelectorProps) {
  const resolved = variant ?? (header ? 'header' : compact ? 'select' : 'select')

  if (resolved === 'header' || resolved === 'hero') {
    return <CityPickerDropdown variant={resolved} className={className} />
  }

  return <CitySelectDropdown className={className} compact={compact} />
}

function CitySelectDropdown({ compact, className }: { compact?: boolean; className?: string }) {
  const { cityId, cityName, setCity } = useCityStore()
  const { cities } = useCities({ withListings: true })
  const sortedCities = useMemo(() => sortCitiesWithSelectedFirst(cities, cityId), [cities, cityId])

  return (
    <Select
      value={String(cityId ?? cities[0]?.id ?? 1)}
      onValueChange={(v) => {
        const city = cities.find((c) => c.id === Number(v))
        if (city) setCity(city.id, city.name)
      }}
    >
      <SelectTrigger
        className={cn(
          'gap-1.5 border-neutral-200 font-medium text-neutral-700',
          compact && 'h-9 w-auto min-w-[120px] text-xs',
          !compact && 'h-10 min-w-[160px]',
          className,
        )}
      >
        <MapPin className="h-3.5 w-3.5 shrink-0 text-neutral-400" />
        <SelectValue placeholder="Select city">{compact ? cityName : `Buy in ${cityName}`}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {sortedCities.map((city) => (
          <SelectItem key={city.id} value={String(city.id)}>
            {city.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function sortCitiesWithSelectedFirst<T extends { id: number; name: string }>(cities: T[], selectedId: number | null) {
  return [...cities].sort((a, b) => {
    if (selectedId != null) {
      if (a.id === selectedId) return -1
      if (b.id === selectedId) return 1
    }
    return a.name.localeCompare(b.name)
  })
}

function CityPickerDropdown({
  variant,
  className,
}: {
  variant: 'header' | 'hero'
  className?: string
}) {
  const { cityId, cityName, setCity, updateCityName } = useCityStore()
  const { cities } = useCities({ withListings: true })
  const location = useLocation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const coords = useAnchoredPosition(open, triggerRef, panelRef, { gap: 6, align: 'start' })
  const hoverOpen = variant === 'header'

  const sortedCities = useMemo(() => sortCitiesWithSelectedFirst(cities, cityId), [cities, cityId])

  useEffect(() => {
    if (!cityId || !cities.length) return
    const match = cities.find((c) => c.id === cityId)
    if (match && match.name !== cityName) updateCityName(match.name)
  }, [cities, cityId, cityName, updateCityName])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return sortedCities
    return cities.filter((c) => c.name.toLowerCase().includes(q))
  }, [cities, sortedCities, query])

  const selectCity = (city: { id: number; name: string; slug: string }) => {
    setCity(city.id, city.name)
    setOpen(false)

    const onListing =
      location.pathname === PATHS.properties ||
      location.pathname.startsWith('/propertylist') ||
      location.pathname.startsWith('/propertylisting') ||
      isSeoLandingPathname(location.pathname)

    if (onListing) {
      const params = new URLSearchParams(location.search)
      const path = location.pathname.toLowerCase()
      let intent = params.get('intent') || ''
      if (/for-rent-in|property-for-rent/.test(path)) intent = 'rent'
      else if (/for-sale-in|property-for-sale/.test(path)) intent = 'buy'

      navigate(
        buildPropertyHref(
          intent === 'rent' ? 'rent' : intent === 'buy' ? 'buy' : '',
          { city: city.slug },
        ),
        { replace: true },
      )
    }
  }

  useEffect(() => {
    if (!open) {
      setQuery('')
      return
    }
    searchRef.current?.focus()
  }, [open])

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (containsTarget(rootRef.current, e.target)) return
      if (containsTarget(panelRef.current, e.target)) return
      setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  useEffect(() => {
    const panel = panelRef.current
    if (!panel || !coords.ready) return
    panel.style.top = `${coords.top}px`
    panel.style.left = `${coords.left}px`
  }, [coords.top, coords.left, coords.ready])

  const isSearchActive = () =>
    searchRef.current === document.activeElement || containsTarget(panelRef.current, document.activeElement)

  const handleEnter = () => {
    if (hoverOpen) setOpen(true)
  }
  const handleLeave = (e: React.MouseEvent) => {
    if (!hoverOpen || isSearchActive()) return
    if (containsTarget(panelRef.current, e.relatedTarget)) return
    setOpen(false)
  }
  const handlePanelLeave = (e: React.MouseEvent) => {
    if (!hoverOpen || isSearchActive()) return
    if (containsTarget(rootRef.current, e.relatedTarget)) return
    setOpen(false)
  }

  const panel =
    open &&
    (typeof document !== 'undefined' ? (
      <div
        ref={panelRef}
        className={cn(
          'fixed z-[110] w-[min(220px,calc(100vw-2rem))] overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-xl',
          !coords.ready && 'invisible',
        )}
        onMouseEnter={hoverOpen ? handleEnter : undefined}
        onMouseLeave={hoverOpen ? handlePanelLeave : undefined}
      >
        <div className="border-b border-neutral-100 p-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400" />
            <Input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search city..."
              className="h-8 border-neutral-200 pl-8 text-xs"
            />
          </div>
        </div>
        <ul className="slim-scrollbar max-h-[220px] overflow-y-auto py-1">
          {filtered.length > 0 ? (
            filtered.map((city) => (
              <li key={city.id}>
                <button
                  type="button"
                  onClick={() => selectCity(city)}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-primary-50 hover:text-primary-700',
                    cityId === city.id && 'bg-primary-50 font-semibold text-primary-700',
                  )}
                >
                  <MapPin className="h-3.5 w-3.5 shrink-0 text-neutral-400" />
                  {city.name}
                </button>
              </li>
            ))
          ) : (
            <li className="px-3 py-4 text-center text-xs text-neutral-400">No city found</li>
          )}
        </ul>
      </div>
    ) : null)

  return (
    <div
      ref={rootRef}
      className={cn('relative shrink-0', className)}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={`Selected city: ${cityName}. Change city`}
        aria-expanded={open}
        className={cn(
          variant === 'hero'
            ? 'flex h-9 max-w-[108px] items-center gap-1 rounded-lg bg-primary-50 px-2 text-[11px] font-semibold text-primary-700 transition-colors hover:bg-primary-100 md:h-11 md:max-w-[124px] md:px-2.5 md:text-xs'
            : 'flex items-center gap-1 border-0 bg-transparent px-0 py-1 text-xs font-medium text-neutral-500 shadow-none transition-colors hover:text-primary-600 xl:text-sm',
          open && variant !== 'hero' && 'text-primary-600',
          open && variant === 'hero' && 'ring-2 ring-primary-200',
        )}
      >
        <MapPin className={cn('shrink-0', variant === 'hero' ? 'h-3.5 w-3.5' : 'h-3.5 w-3.5 opacity-70')} />
        <span className={cn('truncate', variant === 'hero' ? 'max-w-[72px]' : 'max-w-[100px] xl:max-w-[120px]')}>
          {cityName}
        </span>
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 shrink-0 transition-transform',
            variant === 'hero' ? 'opacity-80' : 'opacity-60',
            open && 'rotate-180',
          )}
        />
      </button>

      {hoverOpen && open && <span className="absolute left-0 top-full z-90 h-2 w-full" aria-hidden />}

      {panel ? createPortal(panel, document.body) : null}
    </div>
  )
}
